// Admin module manager (/admin/modules). Nav-gated on me.canManage (the
// route also self-gates; the API enforces qc.manage server-side regardless).
// List/add/edit/deactivate modules and their submodules.

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { QcModule, QcSubmodule } from '@itms-qc/shared';
import { api } from '../lib/api';
import { useApiToken } from '../auth/useApiToken';
import { useMe } from '../lib/me';
import { ModuleBadge } from '../components/badges';
import {
  dangerTextStyle,
  inputStyle,
  labelStyle,
  primaryButtonStyle,
  secondaryButtonStyle,
} from '../components/form';

const cardStyle: React.CSSProperties = {
  background: 'var(--brand-card-bg, white)',
  border: '1px solid var(--brand-border, #D6E4EE)',
  borderRadius: 8,
  padding: '16px 20px',
};

const smallButtonStyle: React.CSSProperties = {
  ...secondaryButtonStyle,
  fontSize: 12,
  padding: '4px 10px',
};

function InactiveTag() {
  return (
    <span
      style={{
        fontSize: 10,
        fontWeight: 500,
        background: '#F1F2F4',
        color: '#4D5560',
        borderRadius: 4,
        padding: '1px 6px',
        textTransform: 'uppercase',
        letterSpacing: '0.4px',
      }}
    >
      Inactive
    </span>
  );
}

export function AdminModulesPage() {
  const getToken = useApiToken();
  const me = useMe();

  const [modules, setModules] = useState<QcModule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Add-module form
  const [newSlug, setNewSlug] = useState('');
  const [newName, setNewName] = useState('');
  const [newSuite, setNewSuite] = useState('');
  const [newIamId, setNewIamId] = useState('');

  // Inline module edit
  const [editId, setEditId] = useState<number | null>(null);
  const [editName, setEditName] = useState('');
  const [editSuite, setEditSuite] = useState('');
  const [editIamId, setEditIamId] = useState('');

  // The catalog mirrors the WISE-IAM registry (38 modules across 11 suites as
  // of 2026-10-15), so one flat list is unreadable. Group by suite, in the
  // order the API already sorts them.
  const suiteGroups = useMemo(() => {
    const groups = new Map<string, QcModule[]>();
    for (const m of modules) {
      const list = groups.get(m.suite) ?? [];
      list.push(m);
      groups.set(m.suite, list);
    }
    return [...groups.entries()];
  }, [modules]);

  // Inline submodule edit
  const [editSubId, setEditSubId] = useState<number | null>(null);
  const [editSubName, setEditSubName] = useState('');

  // Add-submodule form (per module)
  const [addSubModuleId, setAddSubModuleId] = useState<number | null>(null);
  const [newSubSlug, setNewSubSlug] = useState('');
  const [newSubName, setNewSubName] = useState('');

  const refresh = useCallback(async () => {
    try {
      const token = await getToken();
      const { modules: list } = await api.listModules(token, true);
      setModules(list);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load modules');
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function run(action: (token: string) => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      const token = await getToken();
      await action(token);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Action failed');
    } finally {
      setBusy(false);
    }
  }

  function addModule() {
    const slug = newSlug.trim();
    const displayName = newName.trim();
    const suite = newSuite.trim();
    if (!slug || !displayName || !suite) {
      setError('Slug, display name and suite are all required.');
      return;
    }
    const iamModuleId = newIamId.trim().toLowerCase() || null;
    void run(async (token) => {
      await api.createModule(token, { slug, displayName, suite, iamModuleId });
      setNewSlug('');
      setNewName('');
      setNewSuite('');
      setNewIamId('');
    });
  }

  function saveModuleEdit(m: QcModule) {
    const displayName = editName.trim();
    const suite = editSuite.trim();
    if (!displayName || !suite) return;
    const iamModuleId = editIamId.trim().toLowerCase() || null;
    void run(async (token) => {
      await api.updateModule(token, m.id, { displayName, suite, iamModuleId });
      setEditId(null);
    });
  }

  function saveSubmoduleEdit(m: QcModule, s: QcSubmodule) {
    const displayName = editSubName.trim();
    if (!displayName) return;
    void run(async (token) => {
      await api.updateSubmodule(token, m.id, s.id, { displayName });
      setEditSubId(null);
    });
  }

  function addSubmodule(m: QcModule) {
    const slug = newSubSlug.trim();
    const displayName = newSubName.trim();
    if (!slug || !displayName) {
      setError('Submodule slug and display name are both required.');
      return;
    }
    void run(async (token) => {
      await api.createSubmodule(token, m.id, { slug, displayName });
      setAddSubModuleId(null);
      setNewSubSlug('');
      setNewSubName('');
    });
  }

  // me=null while loading; explicit non-manager → no admin surface.
  if (me && !me.canManage) {
    return (
      <div style={{ padding: '24px 0' }}>
        <div style={{ ...cardStyle, fontSize: 13, color: 'var(--brand-text-muted, #8BA4B8)' }}>
          Module management requires the qc.manage permission.
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px 0', display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div>
        <h2 style={{ margin: '0 0 4px', fontSize: 18, fontWeight: 500, color: 'var(--brand-text, #001C3E)' }}>
          Modules
        </h2>
        <p style={{ margin: 0, fontSize: 13, color: 'var(--brand-text-muted, #8BA4B8)' }}>
          The WISE modules cases can be filed against, grouped by suite. Deactivating hides a module from filing; history keeps it.
          The WISE-IAM id is what other apps pass when they deep-link here (<code>?report=1&amp;module=&lt;id&gt;</code>).
        </p>
      </div>

      {error && <div style={dangerTextStyle}>{error}</div>}

      {/* Add module */}
      <div style={cardStyle}>
        <h3 style={{ margin: '0 0 12px', fontSize: 13, fontWeight: 600, color: 'var(--brand-text, #001C3E)' }}>
          Add module
        </h3>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div style={{ width: 200 }}>
            <label style={labelStyle}>
              Slug
              <input
                type="text"
                value={newSlug}
                onChange={(e) => setNewSlug(e.target.value)}
                placeholder="e.g. wise-shell"
                style={{ ...inputStyle, marginTop: 4 }}
              />
            </label>
          </div>
          <div style={{ flex: 1, minWidth: 200 }}>
            <label style={labelStyle}>
              Display name
              <input
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="e.g. WISE Shell"
                style={{ ...inputStyle, marginTop: 4 }}
              />
            </label>
          </div>
          <div style={{ width: 120 }}>
            <label style={labelStyle}>
              Suite
              <input
                type="text"
                value={newSuite}
                onChange={(e) => setNewSuite(e.target.value)}
                placeholder="ITMS / OMS / WMS"
                style={{ ...inputStyle, marginTop: 4 }}
              />
            </label>
          </div>
          <div style={{ width: 180 }}>
            <label style={labelStyle}>
              WISE-IAM id
              <input
                type="text"
                value={newIamId}
                onChange={(e) => setNewIamId(e.target.value)}
                placeholder="e.g. wms-punch (optional)"
                style={{ ...inputStyle, marginTop: 4 }}
              />
            </label>
          </div>
          <button type="button" onClick={addModule} disabled={busy} style={{ ...primaryButtonStyle, opacity: busy ? 0.6 : 1 }}>
            Add module
          </button>
        </div>
      </div>

      {loading && (
        <div style={{ fontSize: 13, color: 'var(--brand-text-muted, #8BA4B8)' }}>Loading modules…</div>
      )}

      {suiteGroups.map(([suite, list]) => (
        <section key={suite} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <h3
            style={{
              margin: '8px 0 0',
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
              color: 'var(--brand-text-muted, #8BA4B8)',
            }}
          >
            {suite} · {list.length}
          </h3>
      {list.map((m) => (
        <div key={m.id} style={{ ...cardStyle, opacity: m.active ? 1 : 0.7 }}>
          {/* Module header */}
          {editId === m.id ? (
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
              <input
                type="text"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                style={{ ...inputStyle, width: 240 }}
                placeholder="Display name"
              />
              <input
                type="text"
                value={editSuite}
                onChange={(e) => setEditSuite(e.target.value)}
                style={{ ...inputStyle, width: 110 }}
                placeholder="Suite"
              />
              <input
                type="text"
                value={editIamId}
                onChange={(e) => setEditIamId(e.target.value)}
                style={{ ...inputStyle, width: 170 }}
                placeholder="WISE-IAM id (optional)"
              />
              <button type="button" onClick={() => saveModuleEdit(m)} disabled={busy} style={primaryButtonStyle}>
                Save
              </button>
              <button type="button" onClick={() => setEditId(null)} style={secondaryButtonStyle}>
                Cancel
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
              <ModuleBadge slug={m.slug} name={m.displayName} />
              <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--brand-text, #001C3E)' }}>
                {m.displayName}
              </span>
              <span style={{ fontSize: 12, color: 'var(--brand-text-muted, #8BA4B8)' }}>
                {m.slug}
                {m.iamModuleId && m.iamModuleId !== m.slug ? ` · IAM ${m.iamModuleId}` : ''}
                {!m.iamModuleId ? ' · no WISE-IAM id' : ''}
              </span>
              {!m.active && <InactiveTag />}
              <span style={{ flex: 1 }} />
              <button
                type="button"
                onClick={() => {
                  setEditId(m.id);
                  setEditName(m.displayName);
                  setEditSuite(m.suite);
                  setEditIamId(m.iamModuleId ?? '');
                }}
                style={smallButtonStyle}
              >
                Edit
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => void run((token) => api.updateModule(token, m.id, { active: !m.active }))}
                style={smallButtonStyle}
              >
                {m.active ? 'Deactivate' : 'Reactivate'}
              </button>
            </div>
          )}

          {/* Submodules */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginLeft: 8 }}>
            {m.submodules.length === 0 && (
              <div style={{ fontSize: 12, color: 'var(--brand-text-muted, #8BA4B8)' }}>No submodules.</div>
            )}
            {m.submodules.map((s) => (
              <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13 }}>
                {editSubId === s.id ? (
                  <>
                    <input
                      type="text"
                      value={editSubName}
                      onChange={(e) => setEditSubName(e.target.value)}
                      style={{ ...inputStyle, width: 220 }}
                    />
                    <button type="button" onClick={() => saveSubmoduleEdit(m, s)} disabled={busy} style={smallButtonStyle}>
                      Save
                    </button>
                    <button type="button" onClick={() => setEditSubId(null)} style={smallButtonStyle}>
                      Cancel
                    </button>
                  </>
                ) : (
                  <>
                    <span style={{ color: 'var(--brand-text, #001C3E)', opacity: s.active ? 1 : 0.6 }}>
                      {s.displayName}
                    </span>
                    <span style={{ fontSize: 11, color: 'var(--brand-text-muted, #8BA4B8)' }}>{s.slug}</span>
                    {!s.active && <InactiveTag />}
                    <span style={{ flex: 1 }} />
                    <button
                      type="button"
                      onClick={() => {
                        setEditSubId(s.id);
                        setEditSubName(s.displayName);
                      }}
                      style={smallButtonStyle}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() =>
                        void run((token) => api.updateSubmodule(token, m.id, s.id, { active: !s.active }))
                      }
                      style={smallButtonStyle}
                    >
                      {s.active ? 'Deactivate' : 'Reactivate'}
                    </button>
                  </>
                )}
              </div>
            ))}

            {/* Add submodule */}
            {addSubModuleId === m.id ? (
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 6, flexWrap: 'wrap' }}>
                <input
                  type="text"
                  value={newSubSlug}
                  onChange={(e) => setNewSubSlug(e.target.value)}
                  placeholder="slug"
                  style={{ ...inputStyle, width: 160 }}
                />
                <input
                  type="text"
                  value={newSubName}
                  onChange={(e) => setNewSubName(e.target.value)}
                  placeholder="Display name"
                  style={{ ...inputStyle, width: 220 }}
                />
                <button type="button" onClick={() => addSubmodule(m)} disabled={busy} style={smallButtonStyle}>
                  Add
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setAddSubModuleId(null);
                    setNewSubSlug('');
                    setNewSubName('');
                  }}
                  style={smallButtonStyle}
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setAddSubModuleId(m.id)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: 12,
                  color: 'var(--brand-text-muted, #8BA4B8)',
                  textDecoration: 'underline',
                  padding: 0,
                  marginTop: 4,
                  alignSelf: 'flex-start',
                }}
              >
                + Add submodule
              </button>
            )}
          </div>
        </div>
      ))}
        </section>
      ))}
    </div>
  );
}
